import { Interface, getAddress, parseUnits, formatUnits } from "ethers";
import { memberWalletRpc, type MemberMessageSigner } from "./member-wallet-rpc";
import { diagnosticFailureClass } from "./value-free-diagnostics";
import type { Id } from "@/convex/_generated/dataModel";
type Account = MemberMessageSigner & {
  sendTransaction(call: { chainId: number; to: `0x${string}`; data: `0x${string}`; value: bigint }): Promise<{transactionHash: string}>;
};
const erc20 = new Interface(['function transfer(address,uint256) returns (bool)', 'function balanceOf(address) view returns (uint256)']);

// KARMA token address on Base (migrated from Polygon 2026-01-30)
// Legacy Polygon: VITE_POLY_KARMA_CHILD_ADDR (0x1C9d4De95C0d60cd8dEf15608892c82082b64ed9)
const KARMA_TOKEN_ADDRESS = (import.meta.env.VITE_KARMA_TOKEN_ADDRESS ||
  "0x0910b5224d0d89f58885d0aeb20d56d1870127cf") as string;

if (!KARMA_TOKEN_ADDRESS) {
  throw new Error("VITE_KARMA_TOKEN_ADDRESS environment variable is not set");
}

// The selected member signer owns sponsorship; this caller supplies only the exact token call.
export const sendGaslessKarma = async (
  account: Account,
  toAddress: string,
  amount: string, // in human-readable form (e.g., "5" not "5000000000000000000")
  contentHistoryId?: Id<"contentHistory">, // for post-specific tipping analytics
) => {
  try {
    const amountWei = parseUnits(amount, 18);
    if (amountWei <= 0n || amountWei >= 2n ** 256n) throw new Error('INVALID_TRANSFER_AMOUNT');
    const result = await account.sendTransaction({
      chainId: 8453,
      to: getAddress(KARMA_TOKEN_ADDRESS) as `0x${string}`,
      data: erc20.encodeFunctionData('transfer', [getAddress(toAddress), amountWei]) as `0x${string}`,
      value: 0n,
    });
    if (!/^0x[0-9a-fA-F]{64}$/.test(result.transactionHash)) throw new Error('TX_HASH_MISSING');

    // AFTER successful transaction, record for analytics
    try {
      const { ConvexHttpClient } = await import("convex/browser");
      const { api } = await import("@/convex/_generated/api");
      const convex = new ConvexHttpClient(
        import.meta.env.VITE_CONVEX_URL as string,
      );

      // Record the completed transaction for analytics
      await convex.mutation(api.tipAnalytics.recordCompletedTip, {
        senderAddress: account.address,
        recipientAddress: toAddress,
        amount: amount,
        txHash: result.transactionHash,
        contentHistoryId: contentHistoryId || undefined,
        tipType: contentHistoryId ? "post" : "user",
      });
    } catch (analyticsError) {
      console.warn(
        "[GaslessWallet] Failed to record analytics (transaction still succeeded):",
        { failureClass: diagnosticFailureClass(analyticsError) },
      );
    }

    // Return format expected by component
    return {
      transactionHash: result.transactionHash,
      queueId: result.transactionHash, // Use transaction hash as reference
    };
  } catch (error) {
    console.error(
      "[GaslessWallet] Failed to send KARMA from user wallet:",
      { failureClass: diagnosticFailureClass(error) },
    );
    throw error;
  }
};

// RPC/data errors remain errors; the caller must not present them as verified zero.
export const getKarmaBalance = async (account: MemberMessageSigner): Promise<string> => {
  const data = erc20.encodeFunctionData('balanceOf', [getAddress(account.address)]);
  const result = await memberWalletRpc(account, 8453, 'eth_call', [{to: getAddress(KARMA_TOKEN_ADDRESS) as `0x${string}`, data}, 'latest']);
  if (typeof result !== 'string' || !/^0x[0-9a-fA-F]{64}$/.test(result)) throw new Error('CHAIN_DATA_UNAVAILABLE');
  return formatUnits(erc20.decodeFunctionResult('balanceOf', result)[0], 18);
};
